/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { UserPreferences } from '@poweralpha/common';
import { type PointerEvent, useRef, useState } from 'react';

export type LauncherPosition = UserPreferences['agent']['launcherPosition'];

// Pointer travel that turns a press into a drag instead of a click.
const DRAG_THRESHOLD_PX = 4;

/** The chip position nearest a drop point: the top half by side, the bottom half bottom-right. */
export const nearestLauncherPosition = (
  x: number,
  y: number,
  width: number,
  height: number,
): LauncherPosition => {
  if (y >= height / 2) return 'bottom-right';
  return x < width / 2 ? 'top-left' : 'top-right';
};

/**
 * Drag-to-reposition for the Agent chip: it follows the pointer while dragged and on release
 * snaps to the nearest position, which `onDrop` saves as Settings › Agent › Agent chip. A press
 * without travel is a click. The chip moves by writing its transform directly: a React render
 * per pointer move would mint a new style rule every frame.
 */
export const useLauncherChipDrag = ({
  onClick,
  onDrop,
}: {
  onClick: () => void;
  onDrop: (position: LauncherPosition) => void;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const gesture = useRef<{ x: number; y: number; dragging: boolean }>(undefined);

  const end = (element: HTMLElement) => {
    gesture.current = undefined;
    element.style.transform = '';
    setIsDragging(false);
  };

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    gesture.current = { x: event.clientX, y: event.clientY, dragging: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const current = gesture.current;
    if (!current) return;
    const x = event.clientX - current.x;
    const y = event.clientY - current.y;
    if (!current.dragging) {
      if (Math.hypot(x, y) <= DRAG_THRESHOLD_PX) return;
      current.dragging = true;
      setIsDragging(true);
    }
    event.currentTarget.style.transform = `translate(${x}px, ${y}px)`;
  };
  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const current = gesture.current;
    if (!current) return;
    end(event.currentTarget);
    if (!current.dragging) {
      onClick();
      return;
    }
    onDrop(
      nearestLauncherPosition(event.clientX, event.clientY, window.innerWidth, window.innerHeight),
    );
  };
  // An aborted gesture (system takeover, lost capture) is neither a click nor a drop.
  const onPointerCancel = (event: PointerEvent<HTMLElement>) => end(event.currentTarget);

  return { isDragging, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } };
};
